import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
  EyeIcon,
  FolderIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/solid';
import {
  createDocumentType,
  createFolder,
  createPacket,
  deleteDocument,
  deletePacket,
  DocumentPacket,
  DocumentTemplateTag,
  getPacketCoach,
  DocumentFolder,
  DocumentItem,
  DocumentPacketSummary,
  DocumentType,
  getDocumentViewUrl,
  listDocumentTypes,
  listDocuments,
  listFolders,
  listPacketsCoach,
  listTemplateTags,
  moveDocument,
  parseTemplateTags,
  uploadDocument,
  ViewUrlResponse,
} from '../services/documentService';
import DocumentViewerPanel from './DocumentViewerPanel';
import DocumentTagsPanel from './DocumentTagsPanel';

interface Client {
  relationship_id: string;
  client_id: string;
  client_name: string;
  client_email: string;
}

interface Props {
  clients: Client[];
}

const fmt = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export const DocumentLibrary: React.FC<Props> = ({ clients }) => {
  const [tab, setTab] = useState<'library' | 'clients'>('library');
  const [showTagsModal, setShowTagsModal] = useState<'system' | 'custom' | null>(null);
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [packets, setPackets] = useState<DocumentPacketSummary[]>([]);
  const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  // Inline add document type
  const [showAddDocType, setShowAddDocType] = useState(false);
  const [newDocTypeName, setNewDocTypeName] = useState('');
  const [addingDocType, setAddingDocType] = useState(false);

  // My Library state
  const [selectedFolderId, setSelectedFolderId] = useState<string | null | 'all'>('all');
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);

  // Upload state (shared)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const clientFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadIsTemplate, setUploadIsTemplate] = useState<boolean | null>(null);
  const [uploadNoConsent, setUploadNoConsent] = useState(false);
  const [uploadDocTags, setUploadDocTags] = useState<string[]>([]);
  const [uploadDocTagsLoading, setUploadDocTagsLoading] = useState(false);
  const DEFAULT_CONSENT_ITEMS = [
    { label: '', required: true },
    { label: '', required: true },
  ];
  const CONSENT_PLACEHOLDERS = [
    'e.g., I consent to AI recording and transcription of my coaching sessions',
    'e.g., I agree to the Terms of Service and Privacy Policy',
  ];
  const [uploadConsentItems, setUploadConsentItems] = useState<Array<{ label: string; required: boolean }>>(DEFAULT_CONSENT_ITEMS);
  const [uploadDocumentTypeId, setUploadDocumentTypeId] = useState('');
  const [uploadDocumentTypeOther, setUploadDocumentTypeOther] = useState('');
  const [uploadFolderId, setUploadFolderId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Client Files state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientUploadFile, setClientUploadFile] = useState<File | null>(null);
  const [clientUploadName, setClientUploadName] = useState('');
  const [clientUploadDocTypeId, setClientUploadDocTypeId] = useState('');
  const [clientUploadDocTypeOther, setClientUploadDocTypeOther] = useState('');
  const [clientUploading, setClientUploading] = useState(false);
  const [clientUploadError, setClientUploadError] = useState('');
  const [showClientUpload, setShowClientUpload] = useState(false);

  // Packet row expand/detail
  const [expandedPacketId, setExpandedPacketId] = useState<string | null>(null);
  const [packetDetails, setPacketDetails] = useState<Record<string, DocumentPacket>>({});
  const [packetDetailLoading, setPacketDetailLoading] = useState<string | null>(null);

  // Packet row copy-link feedback
  const [copiedPacketId, setCopiedPacketId] = useState<string | null>(null);
  const [deletingPacketId, setDeletingPacketId] = useState<string | null>(null);

  // Send packet modal
  // clientId === '' means "pick a client inside the modal" (library-initiated flow)
  const [sendModal, setSendModal] = useState<{ clientId: string; clientName: string; relationshipId: string } | null>(null);
  const [sendModalPickedRelId, setSendModalPickedRelId] = useState('');
  const [packetTitle, setPacketTitle] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [consentGroups, setConsentGroups] = useState<Array<{ docId: string | null; docName: string | null; items: Array<{ label: string; required: boolean }> }>>([]);
  const [sendTagValues, setSendTagValues] = useState<Record<string, string>>({});
  const [sendCustomTags, setSendCustomTags] = useState<DocumentTemplateTag[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  // View panel (single doc from library)
  const [viewer, setViewer] = useState<ViewUrlResponse | null>(null);
  const [viewLoading, setViewLoading] = useState<string | null>(null);

  // Packet viewer (multi-doc slide-out)
  const [packetViewer, setPacketViewer] = useState<{ title: string; docs: Array<{ url: string; fileType: string; docName: string }> } | null>(null);
  const [packetViewLoading, setPacketViewLoading] = useState<string | null>(null);


  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, f, p, t] = await Promise.all([listDocuments(), listFolders(), listPacketsCoach(), listDocumentTypes()]);
      setDocs(d);
      setFolders(f);
      setPackets(p);
      setDocTypes(t);
    } catch (e: any) {
      setError(e.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Derived data ─────────────────────────────────────────────────────────────

  // Coach's own folders (no client_id)
  const coachFolders = folders.filter(f => !f.client_id);

  // Documents in coach's own library (not in a client folder)
  const libraryDocs = docs.filter(d => {
    if (!d.folder_id) return true;
    const folder = folders.find(f => f.id === d.folder_id);
    return !folder?.client_id;
  });

  // Get or find the folder for a specific client
  const getClientFolder = (clientId: string) =>
    folders.find(f => f.client_id === clientId) ?? null;

  // Documents for a specific client (in their tagged folder)
  const getClientDocs = (clientId: string) => {
    const folder = getClientFolder(clientId);
    if (!folder) return [];
    return docs.filter(d => d.folder_id === folder.id);
  };

  // Packets for a specific client
  const getClientPackets = (relationshipId: string) =>
    packets.filter(p => String(p.coach_client_id) === String(relationshipId));

  // Filtered clients for search
  const filteredClients = clients.filter(c =>
    c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.client_email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setUploadFile(f);
    setUploadDocTags([]);
    if (f && !uploadName) setUploadName(f.name.replace(/\.[^.]+$/, ''));
    if (f && uploadIsTemplate && f.name.toLowerCase().endsWith('.docx')) {
      setUploadDocTagsLoading(true);
      try {
        const tags = await parseTemplateTags(f);
        setUploadDocTags(tags);
      } catch { /* ignore */ }
      finally { setUploadDocTagsLoading(false); }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    setUploadError('');
    try {
      const docTypeId = uploadDocumentTypeId !== 'other' ? (uploadDocumentTypeId || undefined) : undefined;
      const docTypeFree = uploadDocumentTypeId === 'other' ? (uploadDocumentTypeOther.trim() || undefined) : undefined;
      const isTemplate = uploadIsTemplate === true;
      const consentItemsToSend = (uploadIsTemplate === false && uploadNoConsent) ? [] : uploadConsentItems.filter(ci => ci.label.trim());
      await uploadDocument(uploadName.trim(), uploadFile, isTemplate, docTypeFree, uploadFolderId || undefined, docTypeId, consentItemsToSend);
      setUploadName(''); setUploadFile(null); setUploadIsTemplate(null); setUploadNoConsent(false); setUploadConsentItems(DEFAULT_CONSENT_ITEMS); setUploadDocTags([]);
      setUploadDocumentTypeId(''); setUploadDocumentTypeOther(''); setUploadFolderId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refresh();
    } catch (e: any) {
      setUploadError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      await createFolder(newFolderName.trim());
      setNewFolderName(''); setShowNewFolderInput(false);
      await refresh();
    } catch (e: any) {
      alert(e.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleClientFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setClientUploadFile(f);
    if (f && !clientUploadName) setClientUploadName(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleClientUpload = async (e: React.FormEvent) => {
    if (!selectedClient || !clientUploadFile || !clientUploadName.trim()) return;
    e.preventDefault();
    setClientUploading(true);
    setClientUploadError('');
    try {
      // Get or create the client's folder
      let folder = getClientFolder(selectedClient.client_id);
      if (!folder) {
        folder = await createFolder(selectedClient.client_name, selectedClient.client_id);
      }
      const cDocTypeId = clientUploadDocTypeId !== 'other' ? (clientUploadDocTypeId || undefined) : undefined;
      const cDocTypeFree = clientUploadDocTypeId === 'other' ? (clientUploadDocTypeOther.trim() || undefined) : undefined;
      await uploadDocument(clientUploadName.trim(), clientUploadFile, false, cDocTypeFree, folder.id, cDocTypeId);
      setClientUploadFile(null); setClientUploadName('');
      setClientUploadDocTypeId(''); setClientUploadDocTypeOther('');
      setShowClientUpload(false);
      if (clientFileInputRef.current) clientFileInputRef.current.value = '';
      await refresh();
    } catch (e: any) {
      setClientUploadError(e.message || 'Upload failed');
    } finally {
      setClientUploading(false);
    }
  };

  const handleMoveDoc = async (docId: string, folderId: string | null) => {
    try {
      const updated = await moveDocument(docId, folderId);
      setDocs(prev => prev.map(d => d.id === updated.id ? updated : d));
    } catch (e: any) { alert(e.message || 'Failed to move'); }
  };


  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await deleteDocument(id);
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch (e: any) { alert(e.message || 'Delete failed'); }
  };

  const handleView = async (docId: string) => {
    setViewLoading(docId);
    try {
      setViewer(await getDocumentViewUrl(docId));
    } catch (e: any) { alert(e.message || 'Could not load document'); }
    finally { setViewLoading(null); }
  };

  const handleViewPacket = async (packetId: string, packetTitle: string) => {
    setPacketViewLoading(packetId);
    try {
      let detail = packetDetails[packetId];
      if (!detail) {
        detail = await getPacketCoach(packetId);
        setPacketDetails(prev => ({ ...prev, [packetId]: detail }));
      }
      const docEntries = await Promise.all(
        detail.items.map(async (item) => {
          const v = await getDocumentViewUrl(item.document_id);
          return { url: v.url, fileType: v.file_type, docName: v.doc_name || item.document?.name || 'Document' };
        })
      );
      setPacketViewer({ title: packetTitle, docs: docEntries });
    } catch (e: any) { alert(e.message || 'Could not load documents'); }
    finally { setPacketViewLoading(null); }
  };

  const openSendModal = async (client: Client) => {
    setSendModal({ clientId: client.client_id, clientName: client.client_name, relationshipId: client.relationship_id });
    setSendModalPickedRelId('');
    setPacketTitle(''); setSelectedDocIds(new Set()); setConsentGroups([]); setSendError(''); setSendSuccess('');
    try {
      const tags = await listTemplateTags();
      setSendCustomTags(tags);
      const defaults: Record<string, string> = {};
      tags.forEach(t => { defaults[t.key] = t.default_value; });
      setSendTagValues(defaults);
    } catch { setSendCustomTags([]); setSendTagValues({}); }
  };

  const handleDeletePacket = async (packetId: string) => {
    if (!window.confirm('Delete this packet? The client will no longer be able to sign it.')) return;
    setDeletingPacketId(packetId);
    try {
      await deletePacket(packetId);
      setPackets(prev => prev.filter(p => p.id !== packetId));
    } catch (e: any) {
      alert(e.message || 'Could not delete packet');
    } finally {
      setDeletingPacketId(null);
    }
  };


  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sendModal) return;
    const isLibFlow = sendModal.clientId === '';
    const relId = isLibFlow ? sendModalPickedRelId : sendModal.relationshipId;
    const clientName = isLibFlow
      ? (clients.find(c => c.relationship_id === relId)?.client_name ?? 'client')
      : sendModal.clientName;
    if (isLibFlow && !relId) { setSendError('Please select a client.'); return; }
    if (!packetTitle.trim()) { setSendError('Please enter a packet title.'); return; }
    if (selectedDocIds.size === 0) { setSendError('Select at least one document.'); return; }
    setSending(true); setSendError(''); setSendSuccess('');
    try {
      const nonEmptyTagValues = Object.fromEntries(Object.entries(sendTagValues).filter(([, v]) => v.trim() !== ''));
      const allConsentItems = consentGroups.flatMap(g => g.items).filter(i => i.label.trim());
      await createPacket(relId, packetTitle.trim(), Array.from(selectedDocIds), allConsentItems.length > 0 ? allConsentItems : undefined, Object.keys(nonEmptyTagValues).length > 0 ? nonEmptyTagValues : undefined);
      setSendSuccess(`Packet sent to ${clientName}!`);
      await refresh();
      setTimeout(() => setSendModal(null), 1500);
    } catch (e: any) { setSendError(e.message || 'Send failed'); }
    finally { setSending(false); }
  };

  const toggleDoc = (id: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setConsentGroups(g => g.filter(group => group.docId !== id));
      } else {
        next.add(id);
        const doc = docs.find(d => d.id === id);
        if (doc?.consent_items && doc.consent_items.length > 0) {
          setConsentGroups(g => [...g, { docId: id, docName: doc.name, items: doc.consent_items!.map((i: any) => ({ ...i })) }]);
        }
      }
      return next;
    });
  };

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  // ── Document row ─────────────────────────────────────────────────────────────

  const DocRow = ({ doc, showFolderControls = true }: { doc: DocumentItem; showFolderControls?: boolean }) => {
    const typeName = docTypes.find(t => t.id === doc.document_type_id)?.name;
    return (
      <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
        <div className="flex items-center gap-3 min-w-0">
          <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
              {doc.is_template && <span className="flex-shrink-0 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">Template</span>}
              {typeName && <span className="flex-shrink-0 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">{typeName}</span>}
            </div>
            <p className="text-xs text-gray-400">{doc.file_name} · {fmt(doc.file_size)} · {fmtDate(doc.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 ml-4 flex-shrink-0">
          <button onClick={() => handleView(doc.id)} disabled={viewLoading === doc.id} className="p-1.5 text-gray-400 hover:text-blue-600 rounded disabled:opacity-50" title="View">
            {viewLoading === doc.id
              ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
              : <EyeIcon className="w-4 h-4" />}
          </button>
          {showFolderControls && coachFolders.length > 0 && (
            <select
              value={doc.folder_id || ''}
              onChange={e => handleMoveDoc(doc.id, e.target.value || null)}
              className="text-xs border border-gray-200 rounded px-1.5 py-1 text-gray-600 bg-white focus:outline-none"
              title="Move to folder"
            >
              <option value="">Unfiled</option>
              {coachFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
          <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" title="Delete">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {/* ── Tabs ── */}
      <div className="flex items-center border-b border-gray-200">
        {([
          { key: 'library' as const, label: 'My Library', Icon: FolderOpenIcon },
          { key: 'clients' as const, label: `Client Files${clients.length > 0 ? ` (${clients.length})` : ''}`, Icon: UserGroupIcon },
        ]).map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
        <div className="ml-auto pb-1">
          <button
            onClick={() => openSendModal({ relationship_id: '', client_id: '', client_name: '', client_email: '' })}
            disabled={docs.length === 0 || clients.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            title={docs.length === 0 ? 'Upload documents first' : clients.length === 0 ? 'No clients yet' : 'Send documents to a client'}
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            Send Documents
          </button>
        </div>
      </div>

      {/* ════════════ MY LIBRARY TAB ════════════ */}
      {tab === 'library' && (
        <div className="flex gap-4">
          {/* Folder sidebar */}
          <div className="w-44 flex-shrink-0">
            <div className="bg-white border rounded-lg overflow-hidden">
              {/* All Documents — top-level */}
              <button
                onClick={() => setSelectedFolderId('all')}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-gray-50 ${selectedFolderId === 'all' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'}`}
              >
                <FolderOpenIcon className="w-4 h-4 flex-shrink-0" />
                All Documents
                <span className="ml-auto text-xs text-gray-400">{libraryDocs.length}</span>
              </button>
              {/* Unfiled + folders — indented children */}
              <button
                onClick={() => setSelectedFolderId(null)}
                className={`w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-gray-50 border-t ${selectedFolderId === null ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500'}`}
              >
                <DocumentIcon className="w-3.5 h-3.5 flex-shrink-0" />
                Unfiled
                <span className="ml-auto text-xs text-gray-400">{libraryDocs.filter(d => !d.folder_id).length}</span>
              </button>
              {coachFolders.map(f => (
                <button
                  key={f.id}
                  onClick={() => setSelectedFolderId(f.id)}
                  className={`w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-gray-50 border-t ${selectedFolderId === f.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500'}`}
                >
                  <FolderIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{f.name}</span>
                  <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{libraryDocs.filter(d => d.folder_id === f.id).length}</span>
                </button>
              ))}
              {/* Template Tags — sibling of All Documents */}
              <div className="border-t">
                <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700">
                  <ClipboardDocumentIcon className="w-4 h-4 flex-shrink-0" />
                  Template Tags
                </div>
                <button
                  onClick={() => setShowTagsModal('system')}
                  className="w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-gray-50 border-t text-gray-500"
                >
                  <span className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 text-xs leading-none">⊙</span>
                  System Tags
                </button>
                <button
                  onClick={() => setShowTagsModal('custom')}
                  className="w-full flex items-center gap-2 pl-7 pr-3 py-2 text-sm text-left hover:bg-gray-50 border-t text-gray-500"
                >
                  <span className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 text-xs leading-none">⊙</span>
                  Custom Tags
                </button>
              </div>
            </div>
            {showNewFolderInput ? (
              <form onSubmit={handleCreateFolder} className="mt-2 flex gap-1">
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Folder name"
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <button type="submit" disabled={creatingFolder} className="px-2 py-1.5 bg-primary-600 text-white text-xs rounded-lg disabled:opacity-50">{creatingFolder ? '…' : 'Add'}</button>
                <button type="button" onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }} className="px-2 py-1.5 text-gray-400 hover:text-gray-600 text-xs">✕</button>
              </form>
            ) : (
              <button onClick={() => setShowNewFolderInput(true)} className="mt-2 w-full flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 hover:text-primary-600 hover:bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <PlusIcon className="w-3.5 h-3.5" /> New Folder
              </button>
            )}
          </div>

          {/* Upload + doc list */}
          <div className="flex-1 min-w-0 space-y-3">

            <form onSubmit={handleUpload} className="p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 space-y-4">
              <p className="text-sm font-medium text-gray-700">Upload a document</p>

              {/* Step 1: template or not */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Is this a template document?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setUploadIsTemplate(true); setUploadFile(null); setUploadDocTags([]); setUploadNoConsent(false); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${uploadIsTemplate === true ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'}`}
                  >
                    Yes, template
                    <p className="text-xs font-normal mt-0.5 opacity-75">Uses <code>{'{{tags}}'}</code> for personalization</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setUploadIsTemplate(false); setUploadFile(null); setUploadDocTags([]); setUploadNoConsent(false); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${uploadIsTemplate === false ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'}`}
                  >
                    No, regular document
                    <p className="text-xs font-normal mt-0.5 opacity-75">Sent as-is to clients</p>
                  </button>
                </div>
              </div>

              {/* Step 2: file + name (only after template choice) */}
              {uploadIsTemplate !== null && (
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input type="text" placeholder="Display name" value={uploadName} onChange={e => setUploadName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" required />
                    <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                      <ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
                      {uploadFile ? uploadFile.name : 'Choose file'}
                      <input ref={fileInputRef} type="file" accept={uploadIsTemplate ? '.docx' : '.pdf,.doc,.docx'} className="hidden" onChange={handleFileChange} />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {coachFolders.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Save to folder</label>
                        <select value={uploadFolderId} onChange={e => setUploadFolderId(e.target.value)}
                          className="block w-40 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none">
                          <option value="">Unfiled</option>
                          {coachFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-600">Document type <span className="text-red-500">*</span></label>
                        {!showAddDocType && (
                          <button type="button" onClick={() => setShowAddDocType(true)} className="text-xs text-primary-600 hover:text-primary-700 font-medium ml-2">+ Add type</button>
                        )}
                      </div>
                      {showAddDocType ? (
                        <div className="flex gap-1">
                          <input autoFocus type="text" value={newDocTypeName} onChange={e => setNewDocTypeName(e.target.value)} placeholder="e.g. Intake Form"
                            className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                            onKeyDown={async e => {
                              if (e.key === 'Escape') { setShowAddDocType(false); setNewDocTypeName(''); }
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (!newDocTypeName.trim() || addingDocType) return;
                                setAddingDocType(true);
                                try { const created = await createDocumentType(newDocTypeName.trim()); setDocTypes(prev => [...prev, created]); setUploadDocumentTypeId(created.id); setNewDocTypeName(''); setShowAddDocType(false); } catch { /* ignore */ } finally { setAddingDocType(false); }
                              }
                            }}
                          />
                          <button type="button" disabled={!newDocTypeName.trim() || addingDocType}
                            onClick={async () => { if (!newDocTypeName.trim() || addingDocType) return; setAddingDocType(true); try { const created = await createDocumentType(newDocTypeName.trim()); setDocTypes(prev => [...prev, created]); setUploadDocumentTypeId(created.id); setNewDocTypeName(''); setShowAddDocType(false); } catch { /* ignore */ } finally { setAddingDocType(false); } }}
                            className="px-2 py-1.5 bg-primary-600 text-white text-xs rounded-lg disabled:opacity-50">{addingDocType ? '…' : 'Add'}</button>
                          <button type="button" onClick={() => { setShowAddDocType(false); setNewDocTypeName(''); }} className="px-2 py-1.5 text-gray-400 hover:text-gray-600 text-xs">✕</button>
                        </div>
                      ) : (
                        <select value={uploadDocumentTypeId} onChange={e => setUploadDocumentTypeId(e.target.value)}
                          className="block w-48 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none">
                          <option value="" disabled>Select a type...</option>
                          {docTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: tags found in document (only for templates after file picked) */}
              {uploadIsTemplate && uploadFile && (
                <div className="p-3 bg-white border border-gray-200 rounded-lg">
                  <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Tags found in this document</p>
                  {uploadDocTagsLoading ? (
                    <p className="text-xs text-gray-400">Scanning document...</p>
                  ) : uploadDocTags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {uploadDocTags.map(tag => (
                        <code key={tag} className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded font-mono">{`{{${tag}}}`}</code>
                      ))}
                    </div>
                  ) : uploadFile.name.toLowerCase().endsWith('.docx') ? (
                    <p className="text-xs text-gray-400 italic">No template tags found. Add <code className="bg-gray-100 px-1 rounded">{'{{tag}}'}</code> placeholders to your DOCX to use personalization.</p>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Tag scanning is only available for .docx files.</p>
                  )}
                </div>
              )}

              {/* Step 4: consent items (shown after file is picked) */}
              {uploadFile && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Client consent items {!uploadNoConsent && <span className="text-red-500">*</span>}
                    </p>
                    {!uploadNoConsent && (
                      <button type="button" onClick={() => setUploadConsentItems(prev => [...prev, { label: '', required: false }])}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium">+ Add item</button>
                    )}
                  </div>
                  {uploadIsTemplate === false && (
                    <label className="flex items-center gap-2 mb-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={uploadNoConsent}
                        onChange={e => {
                          setUploadNoConsent(e.target.checked);
                          if (e.target.checked) setUploadConsentItems(DEFAULT_CONSENT_ITEMS);
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600">No consent required for this document</span>
                    </label>
                  )}
                  {!uploadNoConsent && (
                    <>
                      <p className="text-xs text-gray-400 mb-3">Clients will check each box before they can sign this document.</p>
                      {uploadConsentItems.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-2">
                          <input type="text" value={item.label}
                            onChange={e => setUploadConsentItems(prev => prev.map((ci, i) => i === idx ? { ...ci, label: e.target.value } : ci))}
                            placeholder={CONSENT_PLACEHOLDERS[idx] ?? 'e.g., I consent to...'}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <button type="button" onClick={() => setUploadConsentItems(prev => prev.filter((_, i) => i !== idx))} className="p-1 text-gray-400 hover:text-red-500">
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {uploadError && <p className="text-red-600 text-sm">{uploadError}</p>}

              {/* Upload button: always shown after file picked, disabled until all required fields filled */}
              {uploadFile && (() => {
                const consentOk = (uploadIsTemplate === false && uploadNoConsent) || uploadConsentItems.some(ci => ci.label.trim());
                const blockers = [
                  !uploadName.trim() && 'Add a display name',
                  !uploadDocumentTypeId && 'Select a document type',
                  !consentOk && 'Add at least one consent item',
                ].filter(Boolean) as string[];
                const isBlocked = uploading || blockers.length > 0;
                return (
                  <div title={isBlocked && blockers.length > 0 ? `To upload: ${blockers.join(', ')}` : undefined} className="w-full">
                    <button type="submit" disabled={isBlocked}
                      className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 pointer-events-auto">
                      {uploading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <ArrowUpTrayIcon className="w-4 h-4" />}
                      Upload document
                    </button>
                  </div>
                );
              })()}
            </form>

            {(() => {
              const visible = selectedFolderId === 'all' ? libraryDocs
                : selectedFolderId === null ? libraryDocs.filter(d => !d.folder_id)
                : libraryDocs.filter(d => d.folder_id === selectedFolderId);
              return visible.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <DocumentIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>{libraryDocs.length === 0 ? 'No documents yet.' : 'No documents in this folder.'}</p>
                </div>
              ) : (
                <div className="divide-y border rounded-lg bg-white overflow-hidden">
                  {visible.map(doc => <DocRow key={doc.id} doc={doc} />)}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ════════════ CLIENT FILES TAB ════════════ */}
      {tab === 'clients' && (
        <div className="flex gap-0 border rounded-lg overflow-hidden bg-white">
          {/* Client list sidebar */}
          <div className="w-56 flex-shrink-0 border-r flex flex-col">
            <div className="p-3 border-b">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients…"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredClients.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">{clientSearch ? 'No match' : 'No clients yet'}</p>
              ) : filteredClients.map(c => {
                const clientDocs = getClientDocs(c.client_id);
                const clientPackets = getClientPackets(c.relationship_id);
                const isSelected = selectedClient?.client_id === c.client_id;
                return (
                  <button
                    key={c.client_id}
                    onClick={() => { setSelectedClient(c); setShowClientUpload(false); }}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition-colors ${isSelected ? 'bg-primary-50 border-l-2 border-l-primary-600' : ''}`}
                  >
                    <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary-700' : 'text-gray-900'}`}>{c.client_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {clientDocs.length} file{clientDocs.length !== 1 ? 's' : ''}
                      {clientPackets.length > 0 && ` · ${clientPackets.length} packet${clientPackets.length !== 1 ? 's' : ''}`}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Client detail panel */}
          <div className="flex-1 min-w-0 flex flex-col">
            {!selectedClient ? (
              <div className="flex-1 flex items-center justify-center text-gray-400 py-16">
                <div className="text-center">
                  <UserGroupIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Select a client to view their files</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {/* Client header */}
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedClient.client_name}</h3>
                    <p className="text-xs text-gray-400">{selectedClient.client_email}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openSendModal(selectedClient)}
                      disabled={docs.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg disabled:opacity-40"
                    >
                      <PaperAirplaneIcon className="w-3.5 h-3.5" /> Send Packet
                    </button>
                    <button
                      onClick={() => setShowClientUpload(v => !v)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-lg"
                    >
                      <ArrowUpTrayIcon className="w-3.5 h-3.5" /> Upload File
                    </button>
                  </div>
                </div>

                {/* Upload panel */}
                {showClientUpload && (
                  <form onSubmit={handleClientUpload} className="mx-6 mt-4 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 space-y-3">
                    <p className="text-sm font-medium text-gray-700">Upload file for {selectedClient.client_name}</p>
                    <div className="flex gap-3 flex-wrap">
                      <input type="text" placeholder="Display name" value={clientUploadName} onChange={e => setClientUploadName(e.target.value)}
                        className="flex-1 min-w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" required />
                      <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                        <ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
                        {clientUploadFile ? clientUploadFile.name : 'Choose file'}
                        <input ref={clientFileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleClientFileChange} />
                      </label>
                    </div>
                    <div className="flex gap-3 flex-wrap items-end">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Document type</label>
                        <select value={clientUploadDocTypeId} onChange={e => { setClientUploadDocTypeId(e.target.value); setClientUploadDocTypeOther(''); }}
                          className="block w-44 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none">
                          <option value="">None</option>
                          {docTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          <option value="other">Other…</option>
                        </select>
                        {clientUploadDocTypeId === 'other' && (
                          <input
                            type="text"
                            placeholder="Type name…"
                            value={clientUploadDocTypeOther}
                            onChange={e => setClientUploadDocTypeOther(e.target.value)}
                            className="mt-1.5 block w-44 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        )}
                      </div>
                      <button type="submit" disabled={clientUploading || !clientUploadFile}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-2">
                        {clientUploading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <ArrowUpTrayIcon className="w-4 h-4" />}
                        Upload
                      </button>
                    </div>
                    {clientUploadError && <p className="text-red-600 text-sm">{clientUploadError}</p>}
                  </form>
                )}

                {/* Client documents */}
                <div className="px-6 py-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Files</p>
                  {(() => {
                    const clientDocs = getClientDocs(selectedClient.client_id);
                    return clientDocs.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <DocumentIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No files uploaded for this client yet.</p>
                        <button onClick={() => setShowClientUpload(true)} className="mt-2 text-xs text-primary-600 hover:underline">Upload a file →</button>
                      </div>
                    ) : (
                      <div className="divide-y border rounded-lg bg-white overflow-hidden">
                        {clientDocs.map(doc => <DocRow key={doc.id} doc={doc} showFolderControls={false} />)}
                      </div>
                    );
                  })()}
                </div>

                {/* Sent packets */}
                <div className="px-6 pb-6">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Sent Packets</p>
                  {(() => {
                    const clientPackets = getClientPackets(selectedClient.relationship_id);
                    return clientPackets.length === 0 ? (
                      <p className="text-sm text-gray-400">No packets sent to this client yet.</p>
                    ) : (
                      <div className="divide-y border rounded-lg bg-white overflow-hidden">
                        {clientPackets.map(p => {
                          const isExpanded = expandedPacketId === p.id;
                          const detail = packetDetails[p.id];
                          const isLoadingDetail = packetDetailLoading === p.id;

                          const toggleExpand = async () => {
                            if (isExpanded) { setExpandedPacketId(null); return; }
                            setExpandedPacketId(p.id);
                            if (!packetDetails[p.id]) {
                              setPacketDetailLoading(p.id);
                              try {
                                const d = await getPacketCoach(p.id);
                                setPacketDetails(prev => ({ ...prev, [p.id]: d }));
                              } catch { /* ignore */ }
                              finally { setPacketDetailLoading(null); }
                            }
                          };

                          return (
                            <div key={p.id}>
                              {/* Packet header row */}
                              <button
                                type="button"
                                onClick={toggleExpand}
                                className="w-full flex items-center justify-between px-4 py-3 gap-4 hover:bg-gray-50 text-left"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  {isExpanded
                                    ? <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    : <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{p.title}</p>
                                    <p className="text-xs text-gray-400">Sent {fmtDate(p.sent_at)}</p>
                                    {p.status === 'signed' && (
                                      <p className="text-xs text-green-600 mt-0.5">
                                        Signed {fmtDate(p.signed_at)}
                                        {p.signed_ip_address && ` · IP ${p.signed_ip_address}`}
                                      </p>
                                    )}
                                    {p.status === 'signed_offline' && (
                                      <p className="text-xs text-green-600 mt-0.5">Sent &amp; signed outside CoachSight</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                  {p.status === 'signed' || p.status === 'signed_offline' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                      <CheckCircleIcon className="w-3.5 h-3.5" /> {p.status === 'signed_offline' ? 'Sent & Signed Offline' : 'Signed'}
                                    </span>
                                  ) : (
                                    <>
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                                        <ClockIcon className="w-3.5 h-3.5" /> Pending
                                      </span>
                                      <button
                                        type="button"
                                        title="Copy signing link"
                                        onClick={() => {
                                          const url = `${window.location.origin}/sign/${p.sign_token}`;
                                          navigator.clipboard.writeText(url).then(() => {
                                            setCopiedPacketId(p.id);
                                            setTimeout(() => setCopiedPacketId(null), 2000);
                                          });
                                        }}
                                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-primary-600 border border-gray-200 hover:border-primary-300 rounded-lg transition-colors"
                                      >
                                        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                        {copiedPacketId === p.id ? 'Copied!' : 'Copy link'}
                                      </button>
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    title="View documents"
                                    disabled={packetViewLoading === p.id}
                                    onClick={() => handleViewPacket(p.id, p.title)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-primary-600 border border-gray-200 hover:border-primary-300 rounded-lg disabled:opacity-40 transition-colors"
                                  >
                                    {packetViewLoading === p.id
                                      ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600" />
                                      : <EyeIcon className="w-3.5 h-3.5" />}
                                    View
                                  </button>
                                  <button
                                    type="button"
                                    title="Delete packet"
                                    disabled={deletingPacketId === p.id}
                                    onClick={() => handleDeletePacket(p.id)}
                                    className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-40 transition-colors"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </button>

                              {/* Expanded: documents list */}
                              {isExpanded && (
                                <div className="border-t bg-gray-50 px-4 py-3">
                                  {isLoadingDetail ? (
                                    <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400" />
                                      Loading…
                                    </div>
                                  ) : detail ? (
                                    <div className="space-y-1.5">
                                      {detail.items.map(item => {
                                        const filePath = item.filled_file_path || null;
                                        const docName = item.document?.name || 'Document';
                                        const fileType = item.document?.file_type || 'pdf';
                                        const viewUrl = filePath
                                          ? `${window.location.origin.replace(':3000', ':8000')}/api/documents/view/${p.sign_token}/${item.document_id}`
                                          : null;
                                        return (
                                          <div key={item.id} className="flex items-center justify-between gap-3 py-1">
                                            <div className="flex items-center gap-2 min-w-0">
                                              <DocumentIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                              <span className="text-sm text-gray-800 truncate">{docName}</span>
                                              {item.filled_file_path && (
                                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded flex-shrink-0">Filled</span>
                                              )}
                                            </div>
                                            {viewUrl && fileType === 'pdf' && (
                                              <a
                                                href={viewUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs text-primary-600 hover:underline flex-shrink-0"
                                              >
                                                View
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">Could not load documents.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Template Tags Modal ───────────────────────────────────────────── */}
      {showTagsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
              <div>
                <h3 className="font-semibold text-gray-100">Template Tags</h3>
                <p className="text-xs text-dark-muted mt-0.5">Merge fields you can embed in .docx templates</p>
              </div>
              <button onClick={() => setShowTagsModal(null)} className="p-1.5 text-dark-muted hover:text-gray-300 rounded">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-6">
              <DocumentTagsPanel scrollTo={showTagsModal} />
            </div>
          </div>
        </div>
      )}

      {/* ── Send Packet Modal ──────────────────────────────────────────────── */}
      {sendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-900">
                {sendModal.clientId ? `Send Packet to ${sendModal.clientName}` : 'Send Documents to Client'}
              </h3>
              <button onClick={() => setSendModal(null)} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSend} className="p-5 space-y-4">
              {/* Client picker — shown only when launched from library multi-select */}
              {sendModal.clientId === '' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
                  <select
                    value={sendModalPickedRelId}
                    onChange={e => setSendModalPickedRelId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select a client…</option>
                    {clients.map(c => (
                      <option key={c.relationship_id} value={c.relationship_id}>{c.client_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Packet title</label>
                <input type="text" value={packetTitle} onChange={e => setPacketTitle(e.target.value)} placeholder="e.g., Intake Forms 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select documents from your library</label>
                {libraryDocs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Upload documents to your library first.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {libraryDocs.map(doc => (
                      <label key={doc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={selectedDocIds.has(doc.id)} onChange={() => toggleDoc(doc.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                        <span className="text-sm text-gray-800">{doc.name}</span>
                        <span className="text-xs text-gray-400 ml-auto">{fmt(doc.file_size)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {/* Tag values — only shown if coach has custom tags */}
              {sendCustomTags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template tag values <span className="text-gray-400 font-normal">(override defaults for this send)</span>
                  </label>
                  <div className="space-y-2">
                    {sendCustomTags.map(tag => (
                      <div key={tag.key} className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono flex-shrink-0 w-36 truncate">{`{{${tag.key}}}`}</code>
                        <input
                          type="text"
                          value={sendTagValues[tag.key] ?? tag.default_value}
                          onChange={e => setSendTagValues(prev => ({ ...prev, [tag.key]: e.target.value }))}
                          placeholder={tag.default_value || tag.label}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consent items — grouped by document */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Consent items <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const manualIdx = consentGroups.findIndex(g => g.docId === null);
                      if (manualIdx >= 0) {
                        setConsentGroups(prev => prev.map((g, i) => i === manualIdx ? { ...g, items: [...g.items, { label: '', required: true }] } : g));
                      } else {
                        setConsentGroups(prev => [...prev, { docId: null, docName: null, items: [{ label: '', required: true }] }]);
                      }
                    }}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    + Add item
                  </button>
                </div>
                {consentGroups.map((group, groupIdx) => (
                  <div key={group.docId ?? 'manual'} className="mb-3">
                    {group.docName && (
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <DocumentIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-gray-600 truncate">{group.docName}</span>
                      </div>
                    )}
                    {group.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2 pl-5">
                        <input
                          type="text"
                          value={item.label}
                          onChange={e => setConsentGroups(prev => prev.map((g, gi) => gi === groupIdx ? { ...g, items: g.items.map((ci, i) => i === idx ? { ...ci, label: e.target.value } : ci) } : g))}
                          placeholder="e.g., I consent to AI session recording"
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.required}
                            onChange={e => setConsentGroups(prev => prev.map((g, gi) => gi === groupIdx ? { ...g, items: g.items.map((ci, i) => i === idx ? { ...ci, required: e.target.checked } : ci) } : g))}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          Required
                        </label>
                        <button
                          type="button"
                          onClick={() => setConsentGroups(prev => prev.map((g, gi) => gi === groupIdx ? { ...g, items: g.items.filter((_, i) => i !== idx) } : g).filter(g => g.items.length > 0))}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {sendError && <p className="text-red-600 text-sm">{sendError}</p>}
              {sendSuccess && <p className="text-green-600 text-sm">{sendSuccess}</p>}
              <div className="flex gap-3">
                <button type="button" onClick={() => setSendModal(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={sending} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  {sending ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <PaperAirplaneIcon className="w-4 h-4" />}
                  Send to Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewer && (
        <DocumentViewerPanel url={viewer.url} fileType={viewer.file_type} docName={viewer.doc_name} onClose={() => setViewer(null)} />
      )}

      {packetViewer && (
        <DocumentViewerPanel
          documents={packetViewer.docs}
          title={packetViewer.title}
          onClose={() => setPacketViewer(null)}
        />
      )}
    </div>
  );
};

export default DocumentLibrary;
